import { token } from '@atlaskit/tokens';
import { BodyLarge, BodyNormal, H3, H4, H5 } from '@atlassian/mpac-primitives';
import { css } from '@compiled/react';
import React from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies
import { LiteYoutubeEmbed } from 'react-lite-yt-embed';

import { PartnerBadge } from './PartnerBadge';

import type { BundleConfig } from '../config/bundleConfig';
import type { MarketplacePartnerTierType } from '@atlassian/mpac-types/graphql-gateway-types';

import CloudFortifiedIcon from '../assets/cloud_fortified.svg';
import CloudSecurityIcon from '../assets/cloud_security.svg';
import StarIcon from '../assets/star.svg';
import VideoBg1 from '../assets/video_bg_1.svg';
import VideoBg2 from '../assets/video_bg_2.svg';
import VideoBg3 from '../assets/video_bg_3.svg';
import VideoBg4 from '../assets/video_bg_4.svg';

const appsContainerStyles = css({
  padding: `${token('space.600')} 0 ${token('space.1000')}`,
});

const appsHeaderContainerStyles = css({
  display: 'flex',
  justifyContent: 'center',
});

const appsHeaderStyles = css({
  textAlign: 'center',
  padding: `${token('space.800')} 0`,
  maxWidth: '48rem',
});

const appSectionContainerStyles = css({
  display: 'flex',
  width: '100%',
  marginTop: token('space.800'),
  marginBottom: '128px',
  gap: token('space.800'),
  flexDirection: 'row',
  '@media (max-width: 64rem)': {
    flexDirection: 'column',
    gap: token('space.600'),
    marginBottom: token('space.800'),
  },
});

const appContentStyles = css({
  flex: '1 1 50%',
  textAlign: 'left',
  '@media (max-width: 64rem)': {
    flex: '1 1 100%',
  },
});

const videoContentStyles = css({
  flex: '1 1 50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  minHeight: '340px',
  marginLeft: 0,
  '@media (max-width: 64rem)': {
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    marginLeft: token('space.200'),
    flex: '1 1 100%',
  },
});

const videoWrapperStyles = css({
  position: 'relative',
  width: '100%',
  maxWidth: '500px',
  minHeight: '280px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const videoBgStyles = css({
  position: 'absolute',
  top: token('space.negative.300', '-24px'),
  left: '-18px',
  width: 'calc(95% + 60px)',
  height: 'calc(95% + 75px)',
});

const iframeContainerStyles = css({
  position: 'absolute',
  top: '0',
  left: '0',
  width: '100%',
  height: '100%',
  borderRadius: '8px',
  overflow: 'hidden',
  backgroundColor: '#1D1F23',
  boxShadow: token('elevation.shadow.raised'),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& div': {
    width: '100%',
    height: '100%',
  },
});

const linkStyles = css({
  textDecoration: 'none',
  '&:hover': {
    textDecoration: 'none',
  },
  '&:active': {
    textDecoration: 'none',
  },
  '&:focus-visible': {
    textDecoration: 'none',
  },
});

const appCardStyles = css({
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '20px',
  boxShadow: `0px 0px 1px 0px rgba(30, 31, 33, 0.31), 0px 1px 1px 0px rgba(30, 31, 33, 0.25)`,
  cursor: 'pointer',
  width: '100%',
  maxWidth: '600px',
  marginTop: token('space.400'),
  marginBottom: token('space.400'),
  padding: token('space.250'),
  transition: 'box-shadow 0.2s ease-in-out',
  '&:hover': {
    boxShadow: token('elevation.shadow.overlay'),
    textDecoration: 'none',
  },
  '&:active': {
    background: token('elevation.surface.raised.hovered'),
    boxShadow: token('elevation.shadow.overlay'),
    textDecoration: 'none',
  },
  '&:focus-visible': {
    outline: `${token('space.025')} solid ${token('color.border.focused')}`,
    boxShadow: token('elevation.shadow.overlay'),
    textDecoration: 'none',
  },
});

const appIconStyles = css({
  width: '32px',
  height: '32px',
  marginRight: token('space.150'),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '@media (max-width: 48rem)': {
    marginBottom: token('space.300'),
  },
});

const appInfoStyles = css({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
});

const appTitleStyles = css({
  fontFamily: token('font.family.body'),
  lineHeight: token('space.300'),
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  marginTop: 0,
});

const appVendorStyles = css({
  fontSize: '0.875rem',
  color: token('color.text.subtle'),
  display: 'flex',
  alignItems: 'center',
  marginBottom: token('space.150'),
});

const appPartnerTierStyles = css({
  marginLeft: token('space.050', '4px'),
  display: 'inline-block',
});

const appStatsStyles = css({
  display: 'flex',
  alignItems: 'center',
  fontSize: '0.875rem',
  color: token('color.text.subtle'),
  flexWrap: 'wrap',
  gap: token('space.100'),
});

const ratingsStyles = css({
  display: 'flex',
  alignItems: 'center',
  gap: token('space.050'),
});

const cloudBadgeStyles = css({
  display: 'flex',
  alignItems: 'center',
  fontSize: '0.75rem',
  marginTop: token('space.150'),
  width: 'fit-content',
  fontWeight: 500,
  gap: token('space.025'),
  color: token('color.text'),
});

const h3Styles = css({
  fontWeight: '700',
});

const h4Styles = css({
  fontWeight: '700',
});

const appCardHeaderStyles = css({
  display: 'flex',
  alignItems: 'center',
  marginBottom: token('space.150'),
});

interface AppDetails {
  icon: React.ReactNode;
  title: string;
  vendor: string;
  partnerTier?: MarketplacePartnerTierType;
  rating?: string;
  reviewCount?: string;
  installOrDownloadsText?: string;
  badge?: 'cloud-fortified' | 'cloud-security';
  url: string;
}

interface AppSectionProps {
  title: string;
  description: string;
  appDetails: AppDetails;
  videoId: string;
  videoBg: string;
}

interface AppsSectionProps {
  bundleConfig: BundleConfig;
}

// eslint-disable-next-line complexity
const AppSection: React.FC<AppSectionProps> = ({
  title,
  description,
  appDetails,
  videoId,
  videoBg,
}) => {
  return (
    <div css={appSectionContainerStyles}>
      <div css={appContentStyles}>
        <H4 css={h4Styles}>{title}</H4>
        {description && <BodyNormal>{description}</BodyNormal>}
        <a target="_blank" rel="noopener noreferrer" css={linkStyles} href={appDetails.url}>
          <div css={appCardStyles}>
            <div css={appCardHeaderStyles}>
              <div css={appIconStyles}>{appDetails.icon}</div>
              <H5 css={appTitleStyles}>{appDetails.title}</H5>
            </div>
            <div css={appInfoStyles}>
              <div css={appVendorStyles}>
                {appDetails.vendor}
                <span css={appPartnerTierStyles}>
                  {appDetails.partnerTier && <PartnerBadge partnerTier={appDetails.partnerTier} />}
                </span>
              </div>
              {(appDetails.rating ||
                appDetails.reviewCount ||
                appDetails.installOrDownloadsText) && (
                <div css={appStatsStyles}>
                  {appDetails.rating && (
                    <div css={ratingsStyles}>
                      <span>{appDetails.rating}</span>
                      <img src={StarIcon} alt="Star Rating" width="20" height="20" />
                      {appDetails.reviewCount && <span>({appDetails.reviewCount})</span>}
                    </div>
                  )}
                  {appDetails.installOrDownloadsText && (
                    <>
                      <span>•</span>
                      <span>{appDetails.installOrDownloadsText}</span>
                    </>
                  )}
                </div>
              )}
              {appDetails.badge && (
                <div css={cloudBadgeStyles}>
                  <img
                    src={
                      appDetails.badge === 'cloud-fortified'
                        ? CloudFortifiedIcon
                        : CloudSecurityIcon
                    }
                    alt={
                      appDetails.badge === 'cloud-fortified'
                        ? 'Cloud Fortified'
                        : 'Cloud Security Participant'
                    }
                    width="16"
                    height="16"
                  />
                  <span>
                    {appDetails.badge === 'cloud-fortified'
                      ? 'CLOUD FORTIFIED'
                      : 'CLOUD SECURITY PARTICIPANT'}
                  </span>
                </div>
              )}
            </div>
          </div>
        </a>
      </div>

      <div css={videoContentStyles} data-testid={videoId}>
        <div css={videoWrapperStyles}>
          <img src={videoBg} alt="" css={videoBgStyles} />
          <div css={iframeContainerStyles}>
            {videoId.startsWith('http') ? (
              <img src={videoId} alt={`${appDetails.title} screenshot`} />
            ) : (
              <LiteYoutubeEmbed
                id={videoId}
                mobileResolution="hqdefault"
                desktopResolution="hqdefault"
                noCookie={true}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export const AppsSection: React.FC<AppsSectionProps> = ({ bundleConfig }) => {
  return (
    <div css={appsContainerStyles}>
      <div css={appsHeaderContainerStyles}>
        <div css={appsHeaderStyles}>
          <H3 css={h3Styles}>{bundleConfig.appsSection.title}</H3>
          <BodyLarge>{bundleConfig.appsSection.description}</BodyLarge>
        </div>
      </div>

      {bundleConfig.appsSection.sections.map((section, index) => {
        // Get the corresponding app from the apps array
        const app = bundleConfig.apps[index];
        // Get the corresponding video background
        const videoBg = [VideoBg1, VideoBg2, VideoBg3, VideoBg4][index];

        return (
          <AppSection
            key={index}
            title={section.title}
            description={section.description}
            appDetails={{
              icon: <img src={app.icon} alt={app.iconAlt} width="32" height="32" />,
              url: app.url || '#',
              ...section.appDetails,
            }}
            videoId={section.videoId}
            videoBg={videoBg}
          />
        );
      })}
    </div>
  );
};
